import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { MapTrifold, Phone, EnvelopeSimple, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/* Suggests suppliers whose covered areas include the property's location.
   When jrId is provided, each match gets a one-click "Ask" button that fires
   the standard quote-request email (PDF brief + submission link). */
export default function AreaMatchSuppliers({ town, county, postcode, service = "", limit = 6, jrId, onQuoteRequested, askedEmails = [] }) {
  const [matches, setMatches] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [asking, setAsking] = useState(null);
  const [asked, setAsked] = useState(() => new Set());

  const askedSet = new Set(askedEmails.map(e => (e || "").toLowerCase()));
  const isAsked = (s) => asked.has(s.id) || (s.email && askedSet.has(s.email.toLowerCase()));

  useEffect(() => {
    if (!town && !county && !postcode) { setLoading(false); return; }
    API.get("/suppliers/area-matches", { params: { town: town || "", county: county || "", postcode: postcode || "", service: service || "", limit: 50 } })
      .then(({ data }) => { setMatches(data.matches || []); setCount(data.count || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [town, county, postcode, service]);

  const askForQuote = async (s) => {
    setAsking(s.id);
    try {
      await API.post(`/job-requests/${jrId}/request-vendor-quotes`, { vendor_emails: [s.email] });
      setAsked(prev => new Set(prev).add(s.id));
      toast.success(`Quote request sent to ${s.company_name}`);
      onQuoteRequested?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send quote request"); }
    finally { setAsking(null); }
  };

  if (loading || (!town && !county && !postcode)) return null;

  const visible = showAll ? matches : matches.slice(0, limit);
  const place = town || county || postcode;

  return (
    <div className="border border-border rounded-md p-4" data-testid="area-match-suppliers">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
        <MapTrifold size={14} className="text-primary" /> Area Matches ({count})
        {service && <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-700 dark:text-blue-300 normal-case" data-testid="area-match-service-context">{service}</Badge>}
      </h3>
      {matches.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="area-match-empty">No suppliers cover {place} yet.</p>
      ) : (
        <div className="space-y-2.5">
          {visible.map((s) => (
            <div key={s.id} className="pb-2.5 border-b border-border/60 last:border-0 last:pb-0" data-testid={`area-match-${s.id}`}>
              <div className="flex items-center justify-between gap-2">
                <Link to={`/vendors/${s.id}`} className="text-xs font-semibold text-primary hover:underline truncate" data-testid={`area-match-link-${s.id}`}>
                  {s.company_name}
                </Link>
                <div className="flex items-center gap-1.5 shrink-0">
                  {s.phone && <a href={`tel:${s.phone}`} title={s.phone} className="text-muted-foreground hover:text-foreground"><Phone size={12} /></a>}
                  {s.email && <a href={`mailto:${s.email}`} title={s.email} className="text-muted-foreground hover:text-foreground"><EnvelopeSimple size={12} /></a>}
                </div>
              </div>
              {s.trade ? (
                <div className="text-[10px] text-muted-foreground truncate">
                  {s.trade}
                  {s.trade_match && <Badge className="ml-1.5 text-[8px] px-1 py-0 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 hover:bg-blue-100 align-middle" data-testid={`area-match-trade-${s.id}`}>Trade match</Badge>}
                </div>
              ) : null}
              <div className="flex items-center justify-between gap-2 mt-1">
                <div className="flex flex-wrap gap-1 min-w-0">
                  {(s.matched_areas || []).slice(0, 4).map((a) => (
                    <Badge key={a} variant="secondary" className="text-[9px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 hover:bg-emerald-100">
                      {a}
                    </Badge>
                  ))}
                </div>
                {jrId && s.email && (
                  isAsked(s) ? (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium shrink-0" data-testid={`area-match-asked-${s.id}`}>
                      <CheckCircle size={12} weight="fill" /> Sent
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] shrink-0" disabled={asking === s.id}
                            onClick={() => askForQuote(s)} data-testid={`area-match-ask-${s.id}`}>
                      <PaperPlaneTilt size={11} className="mr-1" /> {asking === s.id ? "Sending..." : "Ask"}
                    </Button>
                  )
                )}
              </div>
            </div>
          ))}
          {count > limit && (
            <Button variant="ghost" size="sm" className="h-6 w-full text-[10px]" onClick={() => setShowAll(v => !v)} data-testid="area-match-toggle">
              {showAll ? "Show fewer" : `Show all ${count}`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
